import React, { useEffect, useMemo, useRef, useState } from 'react';
import { path } from 'ramda';
import { Loader } from 'js/components/common/Loader';
import { tagTypeService } from 'js/services/tagType.service';
import { useUserSelector } from 'js/store/hooks';
import { TAG_TYPE } from 'js/components/system/TagManager/constants';

import { useCafIntegration } from './components/CAF';
import { useXpointIntegration, XPointSDKInstance } from './components/Xpoint';
import { useXpointLiteIntegration, XPointLiteSDKInstance } from './components/XpointLite';
import { useXpointNativeIntegration } from './components/XpointNative';
import { INTEGRATIONS } from './constants';
import * as hooks from './hooks';
import * as constants from './constants';
import * as Types from './types';

import style from './Geofencing.scss';

export const Geofencing = ({ config }: Types.GeofencingOptions) => {
    const userId: string = useUserSelector(path(['userDetails', 'userId'])) ?? '';

    const {
        // general config
        useTransparentPopup = false,
        playerTag = '',
        skipBrowserGeolocation = false,
        intervalPings = true,
        inactiveDetection = true,
        // caf integration
        caf: {
            enabled: cafEnabled,
            sdkUrl = ''
        },
        // xpoint integration
        xpoint: {
            enabled: xpointEnabled,
            sdkUrl: xpointSdkURL = ''
        },
        // xpoint-lite integration
        xpointLite: {
            enabled: xpointLiteEnabled,
            sdkUrl: xpoinLitetSdkURL = ''
        },
        // xpoint native integration
        xpointNative: {
            enabled: xpointNativeEnabled = true
        }
    } = config;

    const triggerTimestampRef = useRef<number | null>(null);
    const [transactionID, setTransactionID] = useState<string | null>(null);
    const [nextTime, setNextTime] = useState(0);
    const [sdk, setSDK] = useState<Types.GeofencingSdkInstance | null>(null);
    const [showOverlay, setShowOverlay] = useState(false);
    const cancelRef = useRef(false);
    const reasonRef = useRef(constants.GEOLOCATION_REASON.UNDEFINED);

    const selectedIntegration = hooks.useSelectedIntegration({
        cafEnabled,
        xpointEnabled,
        xpointLiteEnabled,
        xpointNativeEnabled,
        playerTag
    });

    const geolocationType = useMemo(() => {
        switch (selectedIntegration) {
            case INTEGRATIONS.CAF:
                return 'caf';
            case INTEGRATIONS.XPOINT:
                return 'xpoint';
            case INTEGRATIONS.XPOINT_LITE:
                return 'xpoint-lite';
            case INTEGRATIONS.XPOINT_NATIVE:
                return 'xpoint';
            default:
                return '';
        }
    }, [
        selectedIntegration
    ]);

    const runBrowserGeolocation = hooks.useRunBrowserGeolocation({ useTransparentPopup });

    /**
     * This should be called when geolocation has to be scheduled.
     */
    const triggerGeolocation = hooks.useTriggerGeolocation({
        setTransactionID,
        setNextTime,
        setShowOverlay,
        cancelRef,
        geolocationType,
        reasonRef,
        triggerTimestampRef
    });

    /**
     * This should be called when geolocation has to be scheduled.
     */
    const onError = hooks.useOnError({
        setShowOverlay,
        useTransparentPopup,
        geolocationType
    });

    /**
     * This receives geoPacket from the geolocation provider.
     */
    const receivePacket = hooks.useReceivePacket({
        triggerGeolocation,
        setShowOverlay,
        useTransparentPopup,
        geolocationType,
        triggerTimestampRef
    });

    /**
     * This should be called when SDK is downloaded and initialized.
     * It triggers runBrowserGeolocation and then triggerGeolocation.
     */
    const onConnect = hooks.useOnConnect({
        setShowOverlay,
        triggerGeolocation,
        runBrowserGeolocation,
        geolocationType,
        skipBrowserGeolocation
    });

    /**
     * Integration of CAF.
     */
    const {
        downloadCafSDK,
        requestCafPacket
    } = useCafIntegration({
        sdk,
        enable: selectedIntegration === INTEGRATIONS.CAF,
        sdkUrl,
        transactionID,
        onConnect,
        onError,
        receivePacket,
        geolocationType
    });

    /**
     * Integration of Xpoint.
     */
    const {
        downloadXpointSDK,
        requestXpointPacket
    } = useXpointIntegration({
        sdk: sdk as XPointSDKInstance,
        enable: selectedIntegration === INTEGRATIONS.XPOINT,
        sdkUrl: xpointSdkURL,
        transactionID,
        onConnect,
        useTransparentPopup,
        setShowOverlay,
        receivePacket,
        geolocationType
    });

    /**
     * Integration of Xpoint Lite.
     */
    const {
        requestXpointLitePacket,
        downloadXpointLiteSDK
    } = useXpointLiteIntegration({
        sdk: sdk as XPointLiteSDKInstance,
        enable: selectedIntegration === INTEGRATIONS.XPOINT_LITE,
        sdkUrl: xpoinLitetSdkURL,
        transactionID,
        onConnect,
        receivePacket,
        geolocationType
    });

    /**
     * Integration of Xpoint via Native.
     */
    const {
        requestXpointNativePacket,
        initXpointNativeSDK
    } = useXpointNativeIntegration({
        sdk,
        enable: selectedIntegration === INTEGRATIONS.XPOINT_NATIVE,
        transactionID,
        onConnect,
        onError,
        useTransparentPopup,
        receivePacket,
        triggerGeolocation,
        geolocationType
    });

    const downloadSDK: () => Promise<Types.GeofencingSdkInstance> = useMemo(() => {
        switch (selectedIntegration) {
            case INTEGRATIONS.CAF:
                return downloadCafSDK;
            case INTEGRATIONS.XPOINT:
                return downloadXpointSDK;
            case INTEGRATIONS.XPOINT_LITE:
                return downloadXpointLiteSDK;
            case INTEGRATIONS.XPOINT_NATIVE:
                return initXpointNativeSDK;
            default:
                // intentionally like that so that it does not download anything
                return () => Promise.resolve(Function as Types.GeofencingSdkInstance);
        }
    }, [
        selectedIntegration,
        downloadCafSDK,
        downloadXpointSDK,
        initXpointNativeSDK,
        downloadXpointLiteSDK
    ]);

    const requestPacket = useMemo(() => {
        switch (selectedIntegration) {
            case INTEGRATIONS.CAF:
                return requestCafPacket;
            case INTEGRATIONS.XPOINT:
                return requestXpointPacket;
            case INTEGRATIONS.XPOINT_LITE:
                return requestXpointLitePacket;
            case INTEGRATIONS.XPOINT_NATIVE:
                return requestXpointNativePacket;
            default:
                return () => Promise.reject();
        }
    }, [
        requestCafPacket,
        requestXpointPacket,
        requestXpointNativePacket,
        requestXpointLitePacket,
        selectedIntegration
    ]);

    /**
     * Initial call checks whether there is a need to perform a Geofencing check.
     * If yes, downloads SDK and shows the overlay.
     */
    const initialCall = hooks.useInitialCall({
        setSDK,
        setShowOverlay,
        downloadSDK,
        geolocationType,
        useTransparentPopup
    });

    /**
     * Runs once there is a need to actually call GeoComply.
     */
    hooks.useScheduler({
        sdk,
        nextTime,
        transactionID,
        userId,
        setTransactionID,
        setNextTime,
        triggerGeolocation,
        requestPacket,
        geolocationType,
        triggerTimestampRef,
        intervalPings,
        inactiveDetection
    });

    /**
     * Subscribe to IP change event from ip-changed subscription.
     */
    hooks.useOnIpChange({
        sdk,
        nextTime,
        triggerGeolocation,
        setTransactionID,
        setNextTime,
        triggerTimestampRef
    });

    /**
     * Run once OGW is reconnected due to disconnect.
     */
    hooks.useOnReconnect({
        sdk,
        nextTime,
        triggerGeolocation,
        setTransactionID,
        setNextTime,
        triggerTimestampRef
    });

    /**
     * Run once SDK has been downloaded.
     */
    useEffect(() => {
        if (selectedIntegration === INTEGRATIONS.DISABLED) {
            return;
        }

        if (userId) {
            cancelRef.current = false;

            initialCall();
        }

        return () => {
            cancelRef.current = true;

            // unset current timeout
            triggerTimestampRef.current = null;
            setTransactionID(null);
            setNextTime(0);
            setShowOverlay(false);
            setSDK(null);
        };
    }, [
        userId,
        cancelRef,
        initialCall,
        selectedIntegration
    ]);

    /**
     * Enable listener to subscription to ACLRuleFailure
     */
    hooks.useOnACLRuleFailure({
        sdk,
        nextTime,
        setNextTime,
        setShowOverlay,
        useTransparentPopup,
        setTransactionID,
        triggerGeolocation,
        triggerTimestampRef
    });

    /**
     * Show loading only if it is configured, if player is logged in and only first time.
     */
    if (showOverlay) {
        return <>
            <div className={style.geofencing}>
                <Loader />
            </div>
        </>;
    }

    return null;
};

tagTypeService.registerTagType({ [TAG_TYPE.geofencing]: Geofencing });
